import type { JSX } from 'react'

import { useViewerAccess } from '../hooks/use-viewer-access.ts'
import { restoredDisplayBoot } from '../auth/firebase-auth.ts'
import { useSession } from '../session.tsx'
import { IdleGuard, type IdleGuardProps } from './idle-guard.tsx'

/**
 * SessionIdleGuard — the idle guard, with the display-account exemption.
 *
 * People keep the one-hour rule. A viewer the backend has confirmed as a
 * display account is not signed out for being idle (ADR-0019). On a
 * restored IndexedDB boot the rule waits until that check answers, so a restored
 * kiosk with a stale stamp is not signed out before the network is up. A
 * person on any other path keeps the rule from the first render.
 *
 * Mounted with the session, outside the router, so a page error cannot
 * unmount it.
 *
 * @param props - Passed through to the guard. `suspend` is decided here.
 * @returns The guard.
 */
export function SessionIdleGuard(props: IdleGuardProps): JSX.Element | null {
  const { userName } = useSession()
  const access = useViewerAccess(userName)
  const suspend =
    access.data?.displayAccount === true ||
    (restoredDisplayBoot() && access.data === undefined)

  return <IdleGuard {...props} suspend={suspend} />
}
